import { useContext } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { Language, useTranslations } from "../../hooks/useTranslations";
import {
  SP,
  putQuestionnaire,
  useQuestionnaire,
} from "../../http/serviceportalApi";
import { getRemoteDataStatus } from "../../utils";
import Alert from "../ui/Alert";
import ErrorText from "../ui/ErrorText";
import Form from "../ui/Form";
import LoadingSpinner from "../ui/LoadingSpinner";
import Table from "../ui/Table";
import CustomerRow from "./CustomerRow";
import styles from "./QuestionnaireTab.module.css";

const OFFICES = [
  { city: "Bozen", email: "personalberatung.bozen@hgv.it" },
  { city: "Meran", email: "personalberatung.meran@hgv.it" },
  { city: "Brixen", email: "personalberatung.brixen@hgv.it" },
  { city: "Bruneck", email: "personalberatung.bruneck@hgv.it" },
  { city: "Schlanders", email: "personalberatung.schlanders@hgv.it" },
];

const DEADLINE_MONTH = 8; // August
const DEADLINE_DAY = 31;

// Returns whether the questionnaire input is past
// the August 31 deadline (disabled from September 1 onwards).
const getDeadline = () => {
  const d = new Date();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const disabled =
    month > DEADLINE_MONTH || (month === DEADLINE_MONTH && day > DEADLINE_DAY);
  return { disabled };
};

const formatDeadline = (locale: Language) =>
  new Date(2000, DEADLINE_MONTH - 1, DEADLINE_DAY).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
  });

// Form-specific types that differ from API types to support form state
export interface QuestionnaireFormValues {
  customers: CustomerFormValues[];
}

// CustomerFormValues represents form state where empty string means "not selected"
// This differs from the API type where undefined/null is used for optional fields
export interface CustomerFormValues {
  name: string;
  freelanceInvoices: boolean;
  taxCode?: string;
  revisor: boolean;
  revisorType: SP.RevisorType | ""; // Empty string for unselected state
  revisorTaxCodes: string[]; // Always an array in form state, even if empty
}

interface Props {
  luid: SP.Luid;
}

const QuestionnaireTab = ({ luid }: Props) => {
  const { i18n, currentLanguage } = useTranslations();
  const { disabled } = getDeadline();
  const { data, isValidating, error, mutate } = useQuestionnaire(luid);
  const status = getRemoteDataStatus({ isValidating, error });
  const dispatch = useContext(NotificationDispatch);

  return (
    <>
      {status === "success" && (
        <div>
          {disabled && (
            <div className={styles.disabled}>
              {i18n.economicAdvisorApp.questionnaire.disabled}
              <ul className={styles.list}>
                {OFFICES.map((office) => (
                  <li key={office.city}>
                    {office.city}:{" "}
                    <a href={`mailto:${office.email}`}>{office.email}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!disabled && (
            <Alert>
              {i18n.economicAdvisorApp.questionnaire.deadlineAlert(
                formatDeadline(currentLanguage),
              )}
            </Alert>
          )}
          {!data?.customers.length ? (
            <div className={styles.disabled}>
              {i18n.economicAdvisorApp.questionnaire.noCustomers}
            </div>
          ) : (
            <Form<QuestionnaireFormValues>
              defaultValues={{
                customers: data.customers.map((c) => ({
                  ...c,
                  revisorType: c.revisorType ?? "",
                  revisorTaxCodes: c.revisorTaxCodes ?? [],
                })),
              }}
              disabled={disabled}
              submitText={i18n.economicAdvisorApp.questionnaire.submit}
              onSubmit={() => (formData) => {
                const body: SP.PutQuestionnaireBody = {
                  customers: formData.customers.map((c, i) => {
                    const revisorTaxCodes = c.revisorTaxCodes?.filter(Boolean);
                    return {
                      name: data.customers[i]?.name ?? "",
                      freelanceInvoices: c.freelanceInvoices,
                      taxCode: c.taxCode ?? undefined,
                      revisor: c.revisor,
                      revisorType: c.revisorType || undefined,
                      revisorTaxCodes: revisorTaxCodes?.length
                        ? revisorTaxCodes
                        : undefined,
                    };
                  }),
                };

                return putQuestionnaire(luid, body)
                  .then((updated) => {
                    mutate(updated, { revalidate: false });
                    dispatch(showSuccessNotification());
                  })
                  .catch((err) => dispatch(showErrorNotification(err)));
              }}
            >
              {(formProps) => (
                <section className={styles.section}>
                  <Table
                    head={
                      <tr>
                        <th align="left">
                          {
                            i18n.economicAdvisorApp.questionnaire.columnHeaders
                              .name
                          }
                        </th>
                        <th align="left">
                          {
                            i18n.economicAdvisorApp.questionnaire.columnHeaders
                              .freelanceInvoice
                          }
                        </th>
                        <th align="left">
                          {
                            i18n.economicAdvisorApp.questionnaire.columnHeaders
                              .revisor
                          }
                        </th>
                      </tr>
                    }
                    body={data.customers.map((customer, i) => (
                      <CustomerRow
                        key={i}
                        customer={customer}
                        index={i}
                        disabled={disabled}
                        form={formProps}
                      />
                    ))}
                  />
                </section>
              )}
            </Form>
          )}
        </div>
      )}

      {status === "validating" && (
        <div className={styles.status}>
          <LoadingSpinner />
        </div>
      )}

      {status === "failure" && (
        <div className={styles.status}>
          <ErrorText text={i18n.economicAdvisorApp.questionnaire.loadError} />
        </div>
      )}
    </>
  );
};

export default QuestionnaireTab;
